import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  playersControllerCreatePlayerV1,
  playersControllerFindOneV1,
  playersControllerImportSquadV1,
  playersControllerListPlayersV1,
  playersControllerPreviewImportV1,
  playersControllerRemovePlayerV1,
  playersControllerUpdatePlayerV1,
} from '@/api/generated/players/players';
import { uploadsControllerPresignV1 } from '@/api/generated/uploads/uploads';
import type {
  CreatePlayerDto,
  ImportSquadDto,
  PlayersControllerListPlayersV1Params,
  PresignUploadDto,
  PresignUploadDtoScope,
  UpdatePlayerDto,
} from '@/api/generated/model';
import { toPlayersParams, type PlayersSearch } from '../lib/playersSearch';
import { shortlistKeys } from './useShortlists';

/**
 * Query key factory.
 *
 * The hierarchy matters: invalidating `playerKeys.lists()` after a create
 * refreshes every filtered, sorted and paginated list without discarding
 * cached detail pages. Ad-hoc string keys scattered through components make
 * that impossible, and are how a table stays stale after a successful save.
 *
 * `importPreview` sits under its own root because it is keyed by club rather
 * than by player and is invalidated by a completely different event — an
 * import flips rows to `already_imported`, but creating a player by hand
 * does not.
 */
export const playerKeys = {
  all: ['players'] as const,
  lists: () => [...playerKeys.all, 'list'] as const,
  list: (params: PlayersControllerListPlayersV1Params) =>
    [...playerKeys.lists(), params] as const,
  details: () => [...playerKeys.all, 'detail'] as const,
  detail: (id: string) => [...playerKeys.details(), id] as const,
  importPreviews: () => [...playerKeys.all, 'import-preview'] as const,
  importPreview: (clubId: string) => [...playerKeys.importPreviews(), clubId] as const,
};

export function usePlayersQuery(search: PlayersSearch) {
  const params = toPlayersParams(search);

  return useQuery({
    queryKey: playerKeys.list(params),
    queryFn: ({ signal }) => playersControllerListPlayersV1(params, { signal }),
    // Keeps the current rows on screen while a filter change loads, so the
    // table does not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  });
}

export function usePlayerQuery(id: string) {
  return useQuery({
    queryKey: playerKeys.detail(id),
    queryFn: ({ signal }) => playersControllerFindOneV1(id, { signal }),
  });
}

export function useCreatePlayerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePlayerDto) => playersControllerCreatePlayerV1(dto),
    onSuccess: async (player) => {
      // The response is the complete player, so the detail page needs no
      // follow-up GET if the user opens it straight away.
      queryClient.setQueryData(playerKeys.detail(player.id), player);
      await queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

export function useUpdatePlayerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePlayerDto }) =>
      playersControllerUpdatePlayerV1(id, dto),
    onSuccess: async (player) => {
      queryClient.setQueryData(playerKeys.detail(player.id), player);
      await queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

export function useDeletePlayerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => playersControllerRemovePlayerV1(id),
    onSuccess: async (_result, id) => {
      // Dropped rather than invalidated: refetching the detail page for a
      // player who no longer exists only produces a 404.
      queryClient.removeQueries({ queryKey: playerKeys.detail(id) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: playerKeys.lists() }),
        // A deleted player leaves any shortlist they were on.
        queryClient.invalidateQueries({ queryKey: shortlistKeys.all }),
      ]);
    },
  });
}

/**
 * The squad a club's data provider lists, normalised and ready to review.
 *
 * Idle until a club is chosen. `staleTime` is long because the underlying
 * response is cached for a day on the backend anyway — re-requesting while the
 * user reads the table would only burn the provider's rate limit.
 */
export function useImportPreviewQuery(clubId: string | undefined) {
  return useQuery({
    queryKey: playerKeys.importPreview(clubId ?? ''),
    queryFn: ({ signal }) =>
      playersControllerPreviewImportV1({ clubId: clubId as string }, { signal }),
    enabled: Boolean(clubId),
    staleTime: 5 * 60_000,
  });
}

export function useImportSquadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ImportSquadDto) => playersControllerImportSquadV1(dto),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: playerKeys.lists() }),
        // Imported rows have just flipped to `already_imported`.
        queryClient.invalidateQueries({ queryKey: playerKeys.importPreviews() }),
      ]);
    },
  });
}

/**
 * Uploads a file and resolves to the storage key to persist.
 *
 * Three steps, and the middle one deliberately does not go through
 * `src/api/client.ts`: the PUT goes to S3, not to our API, and attaching our
 * bearer token to a presigned URL would break the signature. This is the one
 * sanctioned `fetch` outside the client.
 *
 * `Content-Type` must be the exact value the presign was issued for, because it
 * is signed into the URL — S3 rejects a mismatch.
 */
export function useFileUploadMutation() {
  return useMutation({
    mutationFn: async ({
      file,
      scope,
    }: {
      file: File;
      scope: PresignUploadDtoScope;
    }): Promise<string> => {
      const dto: PresignUploadDto = {
        scope,
        fileName: file.name,
        // Browsers leave `type` empty for some files; the backend validates it
        // against the scope, so an empty string produces a clear 400 rather
        // than an upload that silently stores the wrong content type.
        contentType: file.type,
        sizeBytes: file.size,
      };

      const presigned = await uploadsControllerPresignV1(dto);

      const response = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': presigned.contentType },
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }

      return presigned.key;
    },
  });
}
